// SSE-based server events (replaces Socket.IO)
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { domToPng } from 'modern-screenshot';
import { groupBy } from '../utils/helpers';
import { AdProviders } from '../types';
import { socket } from '../socket';
import { useDeviceId } from './useDeviceId';
import { queues } from '../config';

type UseSocketEventsReturnType = {
  showUuid: boolean;
  socketConnected: boolean;
};

export const useSocketEvents = (): UseSocketEventsReturnType => {
  const history = useHistory();
  const deviceId = useDeviceId();
  const [showUuid, setShowUuid] = useState(true);
  const [socketConnected, setSocketConnected] = useState(socket.connected);

  const getImage = useCallback(async (requestId?: string) => {
    console.log('taking screenshot...');
    const root = document.getElementsByClassName('app')[0] as HTMLElement | undefined;
    if (!root) {
      await socket.emit('screenshot_data', { requestId, error: 'Root element not found' });
      return undefined;
    }

    try {
      const image = await domToPng(root);
      await socket.emit('screenshot_data', { requestId, image });
    } catch (err) {
      const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Screenshot failed';
      try {
        await socket.emit('screenshot_data', { requestId, error: msg });
      } catch (_) { /* ensure server gets something */ }
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!deviceId) return undefined;

    socket.connect(deviceId);

    const connectCallback = (): void => {
      // console.log('Connected to server (SSE), device id: ', socket.id);
      setSocketConnected(true);
    };

    socket.on('connect', connectCallback);

    socket.on('refresh', (): void => {
      socket.emit('refresh_confirm');
      window.location.reload();
    });

    socket.on('screenshot', (data?: unknown): void => {
      const payload = (data ?? {}) as { requestId?: string };
      getImage(payload?.requestId);
    });

    socket.on('show_uuid', () => {
      console.log('pinged');
      setShowUuid(true);
    });

    socket.on('debug', (data?: unknown): void => {
      const payload = (data ?? {}) as { withScreenChange?: boolean };
      const withScreenChange = payload?.withScreenChange !== false;
      console.log('debugging...');
      const lastRefresh = localStorage.getItem('lastRefresh');
      const debugQueues: Record<string, number> = {};
      const serverQueue = groupBy(queues.current?.serverQueue || [], 'type');
      AdProviders.forEach((provider) => {
        (debugQueues as Record<string, number>)[provider] = (serverQueue as Record<string, unknown[]>)[provider]?.length ?? 0;
      });

      const debugData = {
        lastRefresh,
        queues: debugQueues,
        deviceId,
        appVersion: window.DeviceApi.getAppVersion(),
      };
      socket.emit('debug_data', debugData);

      if (!withScreenChange) return;

      if (history.location.pathname.includes('debug')) {
        history.push('/home');
      } else {
        history.push('/debug');
      }
    });

    socket.on('disconnect', (): void => {
      // console.log('Disconnected from server (SSE)');
      setSocketConnected(false);
    });

    socket.on('streetsAuth', (data?: unknown): void => {
      const payload = (data ?? {}) as { token?: string };
      if (payload?.token) {
        socket.emit('streets_auth_ack', {
          deviceId,
          appVersion: window.DeviceApi?.getAppVersion?.() ?? '',
        });
      }
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('debug');
      socket.off('refresh');
      socket.off('screenshot');
      socket.off('show_uuid');
      socket.off('streetsAuth');
    };
    // Only reconnect when deviceId changes; socket.connect() is idempotent for same id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // screenshot replies are emitted directly from the screenshot handler

  useEffect(() => {
    let uuidTimeout: ReturnType<typeof setTimeout> | undefined;
    if (showUuid && deviceId) {
      uuidTimeout = setTimeout(() => {
        setShowUuid(false);
      }, 15 * 1000);
    }
    return () => {
      if (uuidTimeout) {
        clearTimeout(uuidTimeout);
      }
    };
  }, [showUuid, deviceId]);

  return { showUuid, socketConnected };
};
